import type { ActionTemplateSearchResource, ProcessTemplateVersionSummary } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { useEffect, useState } from "react";
import { Action, ProcessTemplateConsumerAction, type ProcessTemplateConsumerActionDispatcher } from "~/analytics/Analytics";
import type { TemplateWizardPageProps } from "~/areas/platformHub/TemplateWizardDialogLayout";
import TemplateWizardDialogLayout from "~/areas/platformHub/TemplateWizardDialogLayout";
import { UpdateBehaviourContent, VersionSelectionContent } from "~/areas/projects/components/Process/ActionTemplateSelector/ProcessTemplateDialogContent";
import { getVersionMask, UpdateBehaviour, UsageConstraint } from "~/areas/projects/components/Templates/TemplateVersioningSection";
import CustomDialog from "~/components/Dialog/CustomDialog";
interface ProcessTemplateSelectionWizardDialogProps {
    open: boolean;
    template: ActionTemplateSearchResource;
    versionSummary: ProcessTemplateVersionSummary;
    onAddTemplate: (templateId: string, versionMask: string) => void;
    onClose: () => void;
    processTemplateConsumerActionDispatch: ProcessTemplateConsumerActionDispatcher;
}
export function ProcessTemplateSelectionWizardDialog({ open, template, onAddTemplate, onClose, versionSummary, processTemplateConsumerActionDispatch }: ProcessTemplateSelectionWizardDialogProps) {
    const [selectedUsageConstraint, setSelectedUsageConstraint] = useState<UsageConstraint>(template.Prerelease ? UsageConstraint.PreRelease : UsageConstraint.Stable);
    const [selectedUpdateBehaviour, setSelectedUpdateBehaviour] = useState<UpdateBehaviour>(template.Prerelease ? UpdateBehaviour.PreRelease : UpdateBehaviour.Minor);
    const currentVersionMask = selectedUsageConstraint === UsageConstraint.PreRelease || !versionSummary.LatestStableVersion ? "PreRelease" : getVersionMask(versionSummary.LatestStableVersion, selectedUpdateBehaviour);
    useEffect(() => {
        setSelectedUsageConstraint(template.Prerelease ? UsageConstraint.PreRelease : UsageConstraint.Stable);
        setSelectedUpdateBehaviour(template.Prerelease ? UpdateBehaviour.PreRelease : UpdateBehaviour.Minor);
    }, [template.Id, template.Prerelease]);
    const handleUsageConstraintChanged = (value: UsageConstraint) => {
        setSelectedUsageConstraint(value);
    };
    const handleUpdateBehaviourChanged = (value: UpdateBehaviour) => {
        setSelectedUpdateBehaviour(value);
    };
    const pages: TemplateWizardPageProps[] = [];
    if (versionSummary.LatestStableVersion && versionSummary.LatestPreReleaseVersion) {
        pages.push({
            title: "Add Template",
            content: <VersionSelectionContent actionTemplate={template} versionSummary={versionSummary} selectedUsageConstraint={selectedUsageConstraint} onUsageConstraintChanged={handleUsageConstraintChanged}/>,
            actionAvailable: true,
            statusBarLabel: "Version type",
            singlePageSaveLabel: selectedUsageConstraint === UsageConstraint.PreRelease ? "Add Template" : undefined,
            singlePageSavingLabel: selectedUsageConstraint === UsageConstraint.PreRelease ? "Adding Template..." : undefined,
        });
        if (selectedUsageConstraint === UsageConstraint.Stable) {
            pages.push({
                title: "Add Template",
                content: <UpdateBehaviourContent actionTemplate={template} versionSummary={versionSummary} selectedUpdateBehaviour={selectedUpdateBehaviour} onUpdateBehaviourChanged={handleUpdateBehaviourChanged}/>,
                actionAvailable: true,
                statusBarLabel: "Update behaviour",
                singlePageSaveLabel: "Add Template",
                singlePageSavingLabel: "Adding Template...",
            });
        }
    }
    if (versionSummary.LatestStableVersion && !versionSummary.LatestPreReleaseVersion) {
        pages.push({
            title: "Add Template",
            content: <UpdateBehaviourContent actionTemplate={template} versionSummary={versionSummary} selectedUpdateBehaviour={selectedUpdateBehaviour} onUpdateBehaviourChanged={handleUpdateBehaviourChanged}/>,
            actionAvailable: true,
            statusBarLabel: "Update behaviour",
            singlePageSaveLabel: "Add Template",
            singlePageSavingLabel: "Adding Template...",
        });
    }
    if (!open)
        return null;
    return (<CustomDialog open={open} close={onClose} render={(props) => (<TemplateWizardDialogLayout pages={pages} onSaveClick={async () => {
                onAddTemplate(template.Id, currentVersionMask);
                processTemplateConsumerActionDispatch("Process Template Version Preferences Set", {
                    type: "Process Template Version Preferences Set",
                    action: ProcessTemplateConsumerAction.SelectVersionPreference,
                    processTemplateId: template.Id,
                    versioningPreference: selectedUpdateBehaviour,
                    isPreRelease: selectedUpdateBehaviour === UpdateBehaviour.PreRelease,
                });
                processTemplateConsumerActionDispatch("Add Process Template To Process", {
                    type: "Add Process Template To Process",
                    processTemplateId: template.Id,
                    action: Action.Add,
                });
                return true;
            }}/>)}/>);
}
